import React, { useState, useEffect, useRef } from "react";
import {
  useParams,
  useNavigate,
  useLocation as useRouterLocation,
} from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Product, Review } from "@/data/products";
import { useProducts } from "@/context/ProductContext";
import { useAuthContext } from "@/lib/AuthProvider";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useLocation as useGeoLocation } from "@/hooks/useLocation";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  ShoppingCart,
  Minus,
  Plus,
  Check,
  Star,
  Package,
  Send,
  Calendar,
  Clock,
  Edit,
  Trash2,
  AlertCircle,
  Edit2,
  Shield,
  Truck,
  Heart,
  HeartIcon,
  AlertTriangle,
  RefreshCw,
  ServerCrash,
  WifiOff,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { format } from "date-fns";
import LoadingSpinner from "@/components/LoadingSpinner";
import {
  fetchProductById,
  addReview,
  updateReview,
  deleteReview,
  getRelatedProducts,
} from "@/services/productService";
import {
  formatDeliveryTime,
  formatProcessingTime,
} from "@/services/shippingService";
import { useShipping, ShippingTier } from "@/context/ShippingContext";
import { useUserProfile } from "@/context/UserProfileContext";
import AuthModalWrapper from "@/components/AuthModalWrapper";
import ProductImage from "@/components/ProductImage";
import { ProductVariations } from "@/components/ProductVariations";
import ScrollDownFAB from "@/components/ScrollDownFAB";
import ReviewImageUpload from "@/components/ReviewImageUpload";
import ImagePreviewModal from "@/components/ImagePreviewModal";
import ProductCard from "@/components/ProductCard";
import {
  uploadReviewImages,
  deleteReviewImages,
} from "@/services/imageUploadService";

// Helper function to calculate average rating of verified reviews only
const calculateAverageRating = (reviews: Review[]) => {
  if (!reviews || reviews.length === 0) return 0;

  const verifiedReviews = reviews.filter(
    (review) => review.status === "verified"
  );
  if (verifiedReviews.length === 0) return 0;

  const sum = verifiedReviews.reduce((acc, review) => acc + review.rating, 0);
  return (sum / verifiedReviews.length).toFixed(1);
};

// Helper function to get stock status - matching admin portal logic
const getStockStatus = (product: Product) => {
  const stockQuantity = product.stockQuantity || 0;
  const lowStockThreshold = product.lowStockThreshold || 10;

  if (stockQuantity === 0) {
    return {
      status: "Out of Stock",
      color: "bg-red-100 text-red-800",
      isOutOfStock: true,
    };
  }
  if (stockQuantity <= lowStockThreshold) {
    return {
      status: "Low Stock",
      color: "bg-yellow-100 text-yellow-800",
      isOutOfStock: false,
    };
  }
  return {
    status: "In Stock",
    color: "bg-green-100 text-green-800",
    isOutOfStock: false,
  };
};

// Helper function to check if user can write a new review
const canUserWriteNewReview = (
  product: Product | null,
  user: { uid: string } | null
) => {
  if (!product || !user) return false;

  // Check if user has any verified review
  const hasVerifiedReview = product.reviews?.some(
    (review) => review.userId === user.uid && review.status === "verified"
  );

  return !hasVerifiedReview;
};

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const routerLocation = useRouterLocation();
  const { user } = useAuthContext();
  const { addToCart } = useCart();
  const { getProductShippingTiers, getAvailableShippingTiers, loading: shippingContextLoading } =
    useShipping();
  const { userProfile, loading: loadingProfile } = useUserProfile();
  const {
    formatPrice,
    selectedCurrency,
    getConvertedItemPrice,
    formatDisplayAmount,
  } = useCurrency();
  const { isUS } = useGeoLocation();
  const isMobile = useIsMobile();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState("");
  const [addedToCart, setAddedToCart] = useState(false);
  const [reviewText, setReviewText] = useState("");
  const [reviewRating, setReviewRating] = useState(5);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [editingReviewId, setEditingReviewId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authDefaultTab, setAuthDefaultTab] = useState<"login" | "register">(
    "login"
  );
  const [publicName, setPublicName] = useState("");
  const [activeTab, setActiveTab] = useState<string>("reviews");
  const [totalNavHeight, setTotalNavHeight] = useState(112); // Default navbar height
  const [shippingTiers, setShippingTiers] = useState<ShippingTier[]>([]);
  const [reviewImages, setReviewImages] = useState<File[]>([]);
  const [existingReviewImages, setExistingReviewImages] = useState<string[]>(
    []
  );
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [previewImageIndex, setPreviewImageIndex] = useState(0);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState<number | null>(null);
  const [editingQuantity, setEditingQuantity] = useState<boolean>(false);
  const [tempQuantity, setTempQuantity] = useState<string>("");
  const reviewsSectionRef = useRef<HTMLDivElement | null>(null);
  const reviewsTabsRef = useRef<HTMLDivElement | null>(null);
  const hasScrolledToReviews = useRef(false);

  // Listen for navigation height updates from NavBar
  useEffect(() => {
    const handleNavHeightChange = (event: CustomEvent) => {
      setTotalNavHeight(event.detail.height);
    };

    // Listen for height updates from NavBar
    window.addEventListener(
      "navHeightUpdate",
      handleNavHeightChange as EventListener
    );

    return () => {
      window.removeEventListener(
        "navHeightUpdate",
        handleNavHeightChange as EventListener
      );
    };
  }, []);

  useEffect(() => {
    const loadProductDetails = async () => {
      if (!productId) return;

      try {
        setLoading(true);

        // Fetch product details
        const productData = await fetchProductById(productId);

        if (productData) {
          setProduct(productData);
          setSelectedImage(productData.images[0]);


          // Fetch related products
          const related = await getRelatedProducts(productId);
          setRelatedProducts(related);
        } else {
          // If product not found, redirect to products page
          navigate("/products");
        }
      } catch (err) {
        setError("Failed to load product details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadProductDetails();
  }, [productId, navigate]);

  useEffect(() => {
    if (!product || loading) return;
    if (typeof window === "undefined") return;
    const hash = window.location.hash;
    const scrollAfterDelay = (ref: React.RefObject<HTMLDivElement>) => {
      setTimeout(() => {
        if (ref.current) {
          const rect = ref.current.getBoundingClientRect();
          const offset = rect.top + window.pageYOffset - (totalNavHeight + 16);
          window.scrollTo({
            top: offset,
            behavior: "smooth",
          });
        }
      }, 200);
    };
    if (hash === "#reviews" && !hasScrolledToReviews.current) {
      hasScrolledToReviews.current = true;
      setActiveTab(routerLocation.state?.focusReviewTab ? "write-review" : "reviews");
      scrollAfterDelay(
        reviewsTabsRef.current ? reviewsTabsRef : reviewsSectionRef
      );
    }
  }, [product, totalNavHeight, loading, routerLocation.state]);

  useEffect(() => {
    // Initialize public name when user changes
    if (user) {
      setPublicName(user.displayName || user.email?.split("@")[0] || "User");
    }
  }, [user]);

  // Update shipping tiers when product, shipping context, or user profile changes
  useEffect(() => {
    if (product && !shippingContextLoading) {
      // Get user's country from their default address
      let userCountry: string | undefined;
      if (user && userProfile && !loadingProfile && userProfile.addresses?.length) {
        const defaultAddress = userProfile.addresses.find(addr => addr.isDefault) || userProfile.addresses[0];
        userCountry = defaultAddress?.country;
      }

      // Get product-specific shipping tiers
      const productShippingTiers = getProductShippingTiers(
        product.availableShippingOptions
      );

      // If user has no address, show all product tiers
      if (!userCountry) {
        setShippingTiers(productShippingTiers);
        return;
      }

      // Filter product tiers by user's territory
      const territoryFilteredTiers = productShippingTiers.filter(tier => {
        // Check included countries first (takes precedence)
        if (tier.includedCountries && tier.includedCountries.length > 0) {
          return tier.includedCountries.includes(userCountry);
        }
        
        // Check excluded countries
        if (tier.excludedCountries && tier.excludedCountries.length > 0) {
          return !tier.excludedCountries.includes(userCountry);
        }
        
        // No restrictions, available globally
        return true;
      });

      setShippingTiers(territoryFilteredTiers);
    }
  }, [product, shippingContextLoading, getProductShippingTiers, user, userProfile, loadingProfile]);

  const handleQuantityChange = (change: number) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1) {
      setQuantity(newQuantity);
    }
  };

  const handleQuantityClick = () => {
    setEditingQuantity(true);
    setTempQuantity(quantity.toString());
  };

  const handleQuantityInputChange = (value: string) => {
    // Only allow positive integers
    if (value === "" || /^\d+$/.test(value)) {
      setTempQuantity(value);
    }
  };

  const handleQuantitySubmit = () => {
    const newQuantity = parseInt(tempQuantity) || 1;
    if (newQuantity >= 1) {
      setQuantity(newQuantity);
    }
    setEditingQuantity(false);
    setTempQuantity("");
  };

  const handleQuantityCancel = () => {
    setEditingQuantity(false);
    setTempQuantity("");
  };

  const handleAddToCart = () => {
    if (!product) return;

    const stockStatus = getStockStatus(product);

    if (stockStatus.isOutOfStock) {
      toast({
        title: "Out of Stock",
        description: `${product.name} is currently out of stock.`,
        variant: "destructive",
      });
      return;
    }

    addToCart(product, quantity);

    // Show success message
    setAddedToCart(true);

    toast({
      title: "Added to Cart",
      description: `${product.name} has been added to your cart.`,
      variant: "default",
    });

    setTimeout(() => {
      setAddedToCart(false);
    }, 3000);
  };

  const handleSubmitReview = async () => {
    if (!product || !user) return;

    if (!reviewText.trim()) {
      toast({
        title: "Error",
        description: "Please enter a review",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      // Delete marked images from Firebase Storage
      if (imagesToDelete.length > 0) {
        try {
          await deleteReviewImages(imagesToDelete);
        } catch (deleteError) {
          console.error("Error deleting images:", deleteError);
          // Don't block the review update if image deletion fails
        }
      }

      let newImageUrls: string[] = [];

      // Upload new images if any are provided
      if (reviewImages.length > 0) {
        try {
          newImageUrls = await uploadReviewImages(
            reviewImages,
            user.uid,
            product.id
          );
        } catch (imageError) {
          console.error("Error uploading images:", imageError);
          toast({
            title: "Error",
            description: "Failed to upload images. Please try again.",
            variant: "destructive",
          });
          return;
        }
      }

      // Combine existing images (not marked for deletion) with new uploaded images
      const allImageUrls = [...existingReviewImages, ...newImageUrls];

      let updatedProduct;

      // Check if we're editing an existing review
      if (editingReviewId !== null) {
        updatedProduct = await updateReview(
          product.id,
          editingReviewId,
          reviewText,
          reviewRating,
          allImageUrls
        );

        toast({
          title: "Review updated",
          description: "Your review has been updated successfully!",
        });
      } else {
        // Create a new review
        const userAvatar = (publicName || user.displayName || user.email || "U")
          .charAt(0)
          .toUpperCase();

        updatedProduct = await addReview(
          product.id,
          user.uid,
          publicName.trim() ||
            user.displayName ||
            user.email?.split("@")[0] ||
            "User",
          userAvatar,
          reviewText,
          reviewRating,
          allImageUrls
        );

        toast({
          title: "Review submitted",
          description: "Thank you for your feedback!",
        });
      }

      // Update local state
      setProduct(updatedProduct);

      // Reset form
      setReviewText("");
      setReviewRating(5);
      setEditingReviewId(null);
      setReviewImages([]);
      setExistingReviewImages([]);
      setImagesToDelete([]);

      // Switch back to reviews tab
      setActiveTab("reviews");
    } catch (err) {
      console.error("Error submitting review:", err);
      toast({
        title: "Error",
        description: "Failed to submit your review. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditReview = (review: Review) => {
    setEditingReviewId(review.id);
    setReviewText(review.content);
    setReviewRating(review.rating);
    setPublicName(review.name);
    setReviewImages([]); // Clear new images
    setExistingReviewImages(review.images || []); // Set existing images
    setImagesToDelete([]); // Clear deletion list
    setActiveTab("write-review");
  };

  const handleDeleteReview = (reviewId: number) => {
    setReviewToDelete(reviewId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteReview = async () => {
    if (!product || !user || !reviewToDelete) return;

    try {
      setIsSubmitting(true);

      // Delete the review using our service
      const updatedProduct = await deleteReview(product.id, reviewToDelete);

      // Update local state
      setProduct(updatedProduct);

      toast({
        title: "Review deleted",
        description: "Your review has been deleted successfully!",
      });
    } catch (err) {
      console.error("Error deleting review:", err);
      toast({
        title: "Error",
        description: "Failed to delete your review. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      setShowDeleteConfirm(false);
      setReviewToDelete(null);
    }
  };

  const cancelDeleteReview = () => {
    setShowDeleteConfirm(false);
    setReviewToDelete(null);
  };

  const openAuthModal = (tab: "login" | "register") => {
    setAuthDefaultTab(tab);
    setAuthModalOpen(true);
  };

  if (loading) {
    return (
      <div
        className="flex flex-col items-center justify-center"
        style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
      >
        <LoadingSpinner size="lg" />
        <p className="mt-4 text-gray-500">Loading product details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-gray-100"
        style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
      >
        <div className="w-full max-w-lg mx-auto">
          {/* Error Icon Container */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                <ServerCrash className="w-10 h-10 text-red-600" />
              </div>
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-3 h-3 text-white" />
              </div>
            </div>
          </div>

          {/* Error Content */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Product Load Error
            </h2>

            <p className="text-gray-600 mb-6 leading-relaxed">
              We encountered an issue loading this product. This might be a
              temporary problem with our servers.
            </p>

            {/* Error Details */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6 border-l-4 border-red-400">
              <p className="text-sm text-gray-700 font-mono break-words">
                {error}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                className="w-full bg-gradient-to-r from-jamaica-green to-green-600 hover:from-jamaica-green/90 hover:to-green-600/90 text-white font-semibold py-3 transition-all duration-300 transform hover:scale-105 shadow-lg"
                onClick={() => window.location.reload()}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>

              <Button
                variant="outline"
                className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-300"
                onClick={() => navigate("/products")}
              >
                Browse All Products
              </Button>
            </div>
          </div>

          {/* Help Text */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              If the problem persists, please{" "}
              <a
                href="mailto:support@jampackd.store?subject=Product%20Load%20Error%20Report&body=Hello%20JamPack'd%20Support%20Team,%0D%0A%0D%0AI%20encountered%20an%20error%20while%20trying%20to%20view%20a%20product%20on%20your%20website:%0D%0A%0D%0AError%20Details:%0D%0A%0D%0APlease%20help%20me%20resolve%20this%20issue.%0D%0A%0D%0AThank%20you!"
                className="text-jamaica-green hover:text-jamaica-green/80 font-medium transition-colors duration-200"
              >
                contact our support team
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div
        className="flex flex-col items-center justify-center"
        style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
      >
        <p className="text-gray-500">Product not found.</p>
        <Button className="mt-4" onClick={() => navigate("/products")}>
          Browse Products
        </Button>
      </div>
    );
  }

  const averageRating = calculateAverageRating(product.reviews || []);
  const verifiedReviewCount =
    product.reviews?.filter((review) => review.status === "verified").length ||
    0;

  // SEO meta tags
  const pageTitle = product.seoTitle || `${product.name} | JamPack'd`;
  const pageDescription = product.seoDescription || product.description;
  const pageImage = product.image || product.images[0];
  const currentUrl = `${window.location.origin}/products/${product.id}`;

  return (
    <div
      className="flex flex-col"
      style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
    >
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />

        {/* Open Graph Tags for Social Sharing */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={pageImage} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:type" content="product" />
        <meta property="og:site_name" content="JamPack'd" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />

        {/* Product-specific Meta Tags */}
        <meta
          property="product:price:amount"
          content={
            product.onSale && product.onSalePrice
              ? product.onSalePrice.toString()
              : product.price.toString()
          }
        />
        <meta property="product:price:currency" content="USD" />
        <meta property="product:availability" content="in stock" />
        <meta property="product:condition" content="new" />

        {/* Additional SEO Tags */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={currentUrl} />
      </Helmet>

      {/* Auth Modal */}
      <AuthModalWrapper
        isOpen={authModalOpen}
        onOpenChange={setAuthModalOpen}
        defaultTab={authDefaultTab}
      />

      <main className="flex-grow bg-white pb-32 sm:pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          {/* Product Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Product Images */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="aspect-square overflow-hidden rounded-lg border border-gray-200">
                <img
                  src={selectedImage}
                  alt={product.name}
                  className="h-full w-full object-cover"
                />
              </div>
              {/* Image Thumbnails */}
              <div
                id="image-gallery"
                className="flex space-x-2 overflow-x-auto pb-2"
              >
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    className={`aspect-square w-20 rounded-md overflow-hidden border-2 transition-all ${
                      selectedImage === image
                        ? "border-jamaica-green"
                        : "border-gray-200 hover:border-jamaica-yellow"
                    }`}
                    onClick={() => setSelectedImage(image)}
                  >
                    <img
                      src={image}
                      alt={`${product.name} - image ${index + 1}`}
                      className="h-full w-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Product Details */}
            <div>
              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-3">
                {(() => {
                  const stockStatus = getStockStatus(product);
                  return (
                    <>
                      {stockStatus.isOutOfStock && (
                        <span className="inline-block bg-red-100 text-red-800 text-xs font-semibold px-3 py-1 rounded-full border border-red-200">
                          OUT OF STOCK
                        </span>
                      )}
                      {!stockStatus.isOutOfStock && product.isNew && (
                        <span className="inline-block bg-jamaica-green text-white text-xs font-semibold px-3 py-1 rounded-full">
                          NEW
                        </span>
                      )}
                      {!stockStatus.isOutOfStock && product.isBestSeller && (
                        <span className="inline-block bg-jamaica-yellow text-jamaica-black text-xs font-semibold px-3 py-1 rounded-full">
                          BEST SELLER
                        </span>
                      )}
                    </>
                  );
                })()}
              </div>

              <p className="text-3xl font-bold text-gray-900">{product.name}</p>

              {/* Brand and Weight Info */}
              <div className="flex items-center gap-3 mt-3">
                {product.brand && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Brand:</span>
                    <span className="inline-flex items-center text-sm font-medium text-jamaica-green bg-jamaica-green/10 px-3 py-1 rounded-full border border-jamaica-green/20">
                      {product.brand}
                    </span>
                  </div>
                )}
                {product.netWeight && product.weightUnit && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Weight:</span>
                    <span className="inline-flex items-center text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                      {product.netWeight}
                      {product.weightUnit}
                    </span>
                  </div>
                )}
              </div>

              {/* Rating summary */}
              <div className="flex items-center mt-4">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`h-5 w-5 ${
                        i < Math.floor(Number(averageRating))
                          ? "text-jamaica-yellow fill-jamaica-yellow"
                          : i < Math.ceil(Number(averageRating)) &&
                            Number(averageRating) % 1 > 0
                          ? "text-jamaica-yellow fill-jamaica-yellow/50"
                          : "text-gray-300"
                      }`}
                    />
                  ))}
                </div>
                <span className="ml-2 text-sm text-gray-600">
                  {averageRating} ({verifiedReviewCount}{" "}
                  {verifiedReviewCount === 1 ? "review" : "reviews"})
                </span>
              </div>

              <div className="mt-4">
                {product.onSale &&
                product.onSalePrice &&
                product.onSalePrice < product.price ? (
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-jamaica-green">
                      {formatPrice(product.onSalePrice)}{" "}
                      <span className="text-gray-600 text-lg">
                        {selectedCurrency}
                      </span>
                    </span>
                    <span className="text-lg text-gray-500 line-through">
                      {formatPrice(product.price)}
                    </span>
                    <span className="ml-2 bg-jamaica-black text-jamaica-yellow text-xs font-semibold px-3 py-1 rounded-full">
                      SAVE{" "}
                      {formatDisplayAmount(
                        getConvertedItemPrice(product.price) -
                          getConvertedItemPrice(product.onSalePrice)
                      )}
                    </span>
                  </div>
                ) : (
                  <div className="text-2xl font-bold text-gray-900">
                    {formatPrice(product.price)}{" "}
                    <span className="text-gray-600 text-lg">
                      {selectedCurrency}
                    </span>
                  </div>
                )}
              </div>

              {/* Product Variations */}
              {product.variations && product.variations.length > 0 && (
                <div className="mt-6">
                  <ProductVariations
                    variations={product.variations}
                    currentProductId={product.id}
                    onVariationChange={(productId) => {
                      navigate(`/product/${productId}`);
                    }}
                  />
                </div>
              )}

              {/* Quantity Selector - Hidden on mobile */}
              {!isMobile && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-gray-900">
                    Quantity
                  </h3>
                  <div className="flex items-center mt-2 w-32">
                    <button
                      onClick={() => handleQuantityChange(-1)}
                      className="rounded-l-md bg-gray-100 px-3 py-2 text-gray-600 hover:bg-gray-200"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    {editingQuantity ? (
                      <input
                        type="text"
                        value={tempQuantity}
                        onChange={(e) =>
                          handleQuantityInputChange(e.target.value)
                        }
                        onBlur={handleQuantitySubmit}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleQuantitySubmit();
                          } else if (e.key === "Escape") {
                            handleQuantityCancel();
                          }
                        }}
                        className="w-full border-y border-gray-200 px-3 py-2 text-center bg-transparent outline-none focus:ring-0"
                        autoFocus
                      />
                    ) : (
                      <span
                        className="w-full border-y border-gray-200 px-3 py-2 text-center cursor-pointer hover:bg-gray-50 transition-colors inline-block"
                        onClick={handleQuantityClick}
                      >
                        {quantity}
                      </span>
                    )}
                    <button
                      onClick={() => handleQuantityChange(1)}
                      className="rounded-r-md bg-gray-100 px-3 py-2 text-gray-600 hover:bg-gray-200"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Add to Cart Button - Hidden on mobile */}
              {!isMobile && (
                <div className="mt-6 scroll-target">
                  {(() => {
                    const stockStatus = getStockStatus(product);
                    return (
                      <Button
                        onClick={handleAddToCart}
                        disabled={stockStatus.isOutOfStock}
                        className={`w-full py-6 ${
                          stockStatus.isOutOfStock
                            ? "bg-gray-400 text-gray-600 cursor-not-allowed hover:bg-gray-400"
                            : "bg-jamaica-green text-white hover:bg-jamaica-green/90"
                        }`}
                      >
                        <ShoppingCart className="h-5 w-5 mr-2" />
                        {stockStatus.isOutOfStock
                          ? "Out of Stock"
                          : "Add to Cart"}
                      </Button>
                    );
                  })()}
                </div>
              )}

              {/* Success Message */}
              {addedToCart && (
                <Alert className="mt-4 bg-green-50 border-green-200 text-green-800">
                  <AlertDescription className="flex items-center">
                    <Check className="h-4 w-4 mr-2 text-green-600" />
                    Item added to your cart!
                  </AlertDescription>
                </Alert>
              )}

              <div className="mt-4 p-3 bg-jamaica-green/5 border border-jamaica-green/20 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm text-gray-700 flex items-center flex-wrap gap-1">
                    <span className="font-medium text-jamaica-green">
                      Split payments
                    </span>{" "}
                    available at checkout with
                    <span className="flex items-center gap-1">
                      <img
                        src="/payment-logos/affirm.avif"
                        alt="Affirm"
                        className="h-4 object-contain"
                      />
                      Affirm,
                    </span>
                    <span className="flex items-center gap-1">
                      <img
                        src="/payment-logos/afterpay.png"
                        alt="Afterpay"
                        className="h-4 object-contain"
                      />
                      Afterpay &{" "}
                    </span>
                    <span className="flex items-center gap-1">
                      <img
                        src="/payment-logos/klarna.png"
                        alt="Klarna"
                        className="h-4 object-contain"
                      />
                      Klarna.
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Description & Shipping Section - Sleek Row Layout */}
          <div className="mt-16 mb-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Product Description */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-gray-50 to-gray-100/50 px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Package className="h-5 w-5 mr-2 text-jamaica-green" />
                    Product Description
                  </h3>
                </div>
                <div className="p-6">
                  <p className="text-gray-600 leading-relaxed">
                    {product.description}
                  </p>
                </div>
              </div>

              {/* Shipping Options */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-gray-50 to-gray-100/50 px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Clock className="h-5 w-5 mr-2 text-jamaica-green" />
                    Shipping Options
                  </h3>
                </div>
                <div className="p-6">
                  {shippingContextLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="h-4 w-4 animate-spin text-jamaica-green mr-2" />
                      <span className="text-sm text-gray-500">
                        Loading shipping options...
                      </span>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {shippingTiers.length > 0 ? (
                        shippingTiers.map((tier, index) => (
                          <div
                            key={tier.id}
                            className="flex items-start p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-100 hover:border-jamaica-green/30 transition-colors"
                          >
                            <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-jamaica-green/20 flex items-center justify-center mr-2 sm:mr-3 mt-0.5">
                              <span className="text-xs font-bold text-jamaica-green">
                                {index + 1}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 sm:mb-1">
                                <h4 className="text-sm font-medium text-gray-900 mb-1 sm:mb-0">
                                  {tier.name}
                                </h4>
                                <div className="flex flex-col items-start sm:items-end text-sm">
                                  <div className="flex items-center gap-2">
                                    {tier.freeShippingThreshold && (
                                      <div className="flex items-center gap-1 px-2 py-1 bg-green-50 rounded-full border border-green-200">
                                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full"></div>
                                        <span className="text-green-700 text-xs font-medium">
                                          FREE for orders over{" "}
                                          {formatPrice(
                                            tier.freeShippingThreshold
                                          )}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <p className="text-xs text-gray-600 mb-2 leading-relaxed">
                                {tier.description}
                              </p>
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs text-gray-500">
                                <span className="flex items-center">
                                  <Package className="h-3 w-3 mr-1 flex-shrink-0" />
                                  <span className="truncate">
                                    Processing: {formatProcessingTime(tier)}
                                  </span>
                                </span>
                                <span className="flex items-center">
                                  <Truck className="h-3 w-3 mr-1 flex-shrink-0" />
                                  <span className="truncate">
                                    Delivery: {formatDeliveryTime(tier)}
                                  </span>
                                </span>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8">
                          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <Clock className="h-6 w-6 text-gray-400" />
                          </div>
                          <p className="text-sm text-gray-500 font-medium">
                            No shipping options configured
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            Please contact support for assistance
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Product Tabs */}
          <div className="mt-8" ref={reviewsTabsRef}>
            <Tabs
              defaultValue="reviews"
              value={activeTab}
              onValueChange={setActiveTab}
            >
              <TabsList
                className={`grid w-full mb-8 bg-muted ${
                  canUserWriteNewReview(product, user)
                    ? "grid-cols-2"
                    : "grid-cols-1"
                }`}
              >
                <TabsTrigger
                  value="reviews"
                  className="flex items-center gap-2 px-4"
                >
                  <Star className="h-4 w-4" />
                  <span className="hidden md:inline">Reviews</span>
                </TabsTrigger>
                {canUserWriteNewReview(product, user) && (
                  <TabsTrigger
                    value="write-review"
                    className="flex items-center gap-2 px-4"
                  >
                    <Send className="h-4 w-4" />
                    <span className="hidden md:inline">Write a Review</span>
                  </TabsTrigger>
                )}
              </TabsList>
              <TabsContent
                value="reviews"
                className="p-6 border rounded-b-md mt-2"
              >
                <div
                  id="reviews-section"
                  ref={reviewsSectionRef}
                  className="space-y-6"
                >
                  {!product.reviews ||
                  product.reviews.length === 0 ||
                  product.reviews.filter(
                    (review) =>
                      review.status === "verified" ||
                      (user && review.userId === user.uid)
                  ).length === 0 ? (
                    <p className="text-gray-500 italic">
                      No reviews yet. Be the first to review this product!
                    </p>
                  ) : (
                    <>
                      {/* User's pending reviews section */}
                      {user &&
                        product.reviews.some(
                          (review) =>
                            review.userId === user.uid &&
                            review.status === "pending"
                        ) && (
                          <div className="mb-8">
                            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-dashed border-jamaica-yellow">
                              <div className="h-8 w-8 rounded-full bg-jamaica-yellow/20 flex items-center justify-center">
                                <span className="text-jamaica-black text-sm font-bold">
                                  !
                                </span>
                              </div>
                              <h3 className="text-base font-medium text-jamaica-black">
                                Your Pending Reviews
                              </h3>
                            </div>
                            {product.reviews
                              .filter(
                                (review) =>
                                  review.userId === user.uid &&
                                  review.status === "pending"
                              )
                              .map((review) => (
                                <div
                                  key={review.id}
                                  className="border-b border-gray-200 pb-6 last:border-0 mt-6"
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                      <div className="flex-shrink-0">
                                        <div className="h-10 w-10 rounded-full bg-jamaica-green/10 flex items-center justify-center text-jamaica-green font-bold">
                                          {review.avatar}
                                        </div>
                                      </div>
                                      <div className="ml-3">
                                        <h4 className="text-sm font-medium text-gray-900 flex items-center">
                                          {review.name}
                                          {review.status === "pending" ? (
                                            <span className="ml-2 bg-jamaica-yellow text-jamaica-black text-xs font-semibold px-2 py-0.5 rounded-full">
                                              Pending
                                            </span>
                                          ) : (
                                            <span className="ml-2 bg-jamaica-green text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                                              Verified
                                            </span>
                                          )}
                                        </h4>
                                        <div className="flex items-center mt-1">
                                          {[...Array(5)].map((_, i) => (
                                            <Star
                                              key={i}
                                              className={`h-4 w-4 ${
                                                i < review.rating
                                                  ? "text-jamaica-yellow fill-jamaica-yellow"
                                                  : "text-gray-300"
                                              }`}
                                            />
                                          ))}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1 flex items-center">
                                          <Calendar className="h-3 w-3 mr-1" />
                                          {format(
                                            new Date(review.date),
                                            "MMM d, yyyy"
                                          )}
                                        </p>
                                      </div>
                                    </div>
                                    {user && review.userId === user.uid && (
                                      <div className="flex space-x-2">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() =>
                                            handleEditReview(review)
                                          }
                                        >
                                          <Edit className="h-3 w-3 mr-1" />
                                          Edit
                                        </Button>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                          onClick={() =>
                                            handleDeleteReview(review.id)
                                          }
                                        >
                                          <Trash2 className="h-3 w-3 mr-1" />
                                          Delete
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                  <div className="mt-3">
                                    <p className="text-sm text-gray-600">
                                      {review.content}
                                    </p>
                                    {/* Display review images if any */}
                                    {review.images &&
                                      review.images.length > 0 && (
                                        <div className="mt-3">
                                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                            {review.images.map(
                                              (image, index) => (
                                                <div
                                                  key={index}
                                                  className="relative group cursor-pointer"
                                                >
                                                  <img
                                                    src={image}
                                                    alt={`Review image ${
                                                      index + 1
                                                    }`}
                                                    className="w-full h-32 object-cover rounded-lg border-2 border-gray-200 group-hover:border-jamaica-green transition-all duration-300 group-hover:shadow-lg group-hover:scale-[1.02]"
                                                    onClick={() => {
                                                      setPreviewImages(
                                                        review.images || []
                                                      );
                                                      setPreviewImageIndex(
                                                        index
                                                      );
                                                      setIsPreviewModalOpen(
                                                        true
                                                      );
                                                    }}
                                                  />
                                                  {/* Click indicator overlay */}
                                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 rounded-lg flex items-center justify-center pointer-events-none">
                                                    <div className="bg-white/90 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
                                                      <svg
                                                        className="w-5 h-5 text-jamaica-green"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                      >
                                                        <path
                                                          strokeLinecap="round"
                                                          strokeLinejoin="round"
                                                          strokeWidth={2}
                                                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                                                        />
                                                      </svg>
                                                    </div>
                                                  </div>
                                                  {/* Image count indicator */}
                                                  {review.images &&
                                                    review.images.length >
                                                      1 && (
                                                      <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full">
                                                        {index + 1}/
                                                        {review.images.length}
                                                      </div>
                                                    )}
                                                </div>
                                              )
                                            )}
                                          </div>
                                          <p className="text-xs text-gray-500 mt-2 flex items-center">
                                            <svg
                                              className="w-3 h-3 mr-1"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                              />
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                              />
                                            </svg>
                                            Click images to view full size
                                          </p>
                                        </div>
                                      )}
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}

                      {/* Verified reviews section */}
                      {product.reviews.some(
                        (review) => review.status === "verified"
                      ) && (
                        <div>
                          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-dashed border-jamaica-green">
                            <div className="h-8 w-8 rounded-full bg-jamaica-green/20 flex items-center justify-center">
                              <span className="text-jamaica-green text-sm font-bold">
                                ✓
                              </span>
                            </div>
                            <h3 className="text-base font-medium text-jamaica-green">
                              Verified Customer Reviews
                            </h3>
                          </div>
                          {product.reviews
                            .filter((review) => review.status === "verified")
                            .sort(
                              (a, b) =>
                                new Date(b.date).getTime() -
                                new Date(a.date).getTime()
                            )
                            .map((review) => (
                              <div
                                key={review.id}
                                className="border-b border-gray-200 pb-6 last:border-0 mt-6"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                      <div className="h-10 w-10 rounded-full bg-jamaica-green/10 flex items-center justify-center text-jamaica-green font-bold">
                                        {review.avatar}
                                      </div>
                                    </div>
                                    <div className="ml-3">
                                      <h4 className="text-sm font-medium text-gray-900 flex items-center">
                                        {review.name}
                                        {review.status === "pending" ? (
                                          <span className="ml-2 bg-jamaica-yellow text-jamaica-black text-xs font-semibold px-2 py-0.5 rounded-full">
                                            Pending
                                          </span>
                                        ) : (
                                          <span className="ml-2 bg-jamaica-green text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                                            Verified
                                          </span>
                                        )}
                                      </h4>
                                      <div className="flex items-center mt-1">
                                        {[...Array(5)].map((_, i) => (
                                          <Star
                                            key={i}
                                            className={`h-4 w-4 ${
                                              i < review.rating
                                                ? "text-jamaica-yellow fill-jamaica-yellow"
                                                : "text-gray-300"
                                            }`}
                                          />
                                        ))}
                                      </div>
                                      <p className="text-xs text-gray-500 mt-1 flex items-center">
                                        <Calendar className="h-3 w-3 mr-1" />
                                        {format(
                                          new Date(review.date),
                                          "MMM d, yyyy"
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                  {user && review.userId === user.uid && (
                                    <div className="flex space-x-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleEditReview(review)}
                                      >
                                        <Edit className="h-3 w-3 mr-1" />
                                        Edit
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                        onClick={() =>
                                          handleDeleteReview(review.id)
                                        }
                                      >
                                        <Trash2 className="h-3 w-3 mr-1" />
                                        Delete
                                      </Button>
                                    </div>
                                  )}
                                </div>
                                <div className="mt-3">
                                  <p className="text-sm text-gray-600">
                                    {review.content}
                                  </p>
                                  {/* Display review images if any */}
                                  {review.images &&
                                    review.images.length > 0 && (
                                      <div className="mt-3">
                                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                          {review.images.map((image, index) => (
                                            <div
                                              key={index}
                                              className="relative group cursor-pointer"
                                            >
                                              <img
                                                src={image}
                                                alt={`Review image ${
                                                  index + 1
                                                }`}
                                                className="w-full h-32 object-cover rounded-lg border-2 border-gray-200 group-hover:border-jamaica-green transition-all duration-300 group-hover:shadow-lg group-hover:scale-[1.02]"
                                                onClick={() => {
                                                  setPreviewImages(
                                                    review.images || []
                                                  );
                                                  setPreviewImageIndex(index);
                                                  setIsPreviewModalOpen(true);
                                                }}
                                              />
                                              {/* Click indicator overlay */}
                                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 rounded-lg flex items-center justify-center pointer-events-none">
                                                <div className="bg-white/90 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
                                                  <svg
                                                    className="w-5 h-5 text-jamaica-green"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                  >
                                                    <path
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                      strokeWidth={2}
                                                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                                                    />
                                                  </svg>
                                                </div>
                                              </div>
                                              {/* Image count indicator */}
                                              {review.images &&
                                                review.images.length > 1 && (
                                                  <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full">
                                                    {index + 1}/
                                                    {review.images.length}
                                                  </div>
                                                )}
                                            </div>
                                          ))}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2">
                                          Click images to view full size
                                        </p>
                                      </div>
                                    )}
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </>
                  )}

                  {/* Message for users who already have a verified review */}
                  {user && !canUserWriteNewReview(product, user) && (
                    <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center">
                        <CheckCircle2 className="h-5 w-5 text-blue-600 mr-2" />
                        <div>
                          <h4 className="text-sm font-medium text-blue-900">
                            You've already reviewed this product
                          </h4>
                          <p className="text-sm text-blue-700 mt-1">
                            You have a verified review for this product. To make
                            changes, use the "Edit" button on your existing
                            review.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
              {canUserWriteNewReview(product, user) && (
                <TabsContent
                  value="write-review"
                  className="p-6 border rounded-b-md mt-2"
                >
                  {!user ? (
                    <div className="space-y-4 text-center p-6">
                      <AlertCircle className="h-12 w-12 text-amber-500 mx-auto" />
                      <h3 className="text-lg font-medium text-gray-900">
                        Authentication Required
                      </h3>
                      <p className="text-gray-600">
                        You need to be logged in to leave a review. Please sign
                        in or create an account.
                      </p>
                      <div className="flex justify-center space-x-4 mt-4">
                        <Button
                          onClick={() => openAuthModal("login")}
                          className="bg-jamaica-green text-white hover:bg-jamaica-green/90"
                        >
                          Sign In
                        </Button>
                        <Button
                          onClick={() => openAuthModal("register")}
                          variant="outline"
                          className="border-jamaica-green text-jamaica-green hover:bg-jamaica-green/10"
                        >
                          Create Account
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        {editingReviewId !== null
                          ? "Edit Your Review"
                          : "Write Your Review"}
                      </h3>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Public Name
                        </label>
                        <Input
                          type="text"
                          value={publicName}
                          onChange={(e) => setPublicName(e.target.value)}
                          placeholder="Your public display name"
                          className="mt-1"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          This name will be visible alongside your review
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Rating
                        </label>
                        <div className="flex items-center mt-2 space-x-1">
                          {[...Array(5)].map((_, index) => {
                            const ratingValue = index + 1;
                            return (
                              <button
                                type="button"
                                key={index}
                                onClick={() => setReviewRating(ratingValue)}
                                className="focus:outline-none"
                              >
                                <Star
                                  className={`h-6 w-6 ${
                                    ratingValue <= reviewRating
                                      ? "text-jamaica-yellow fill-jamaica-yellow"
                                      : "text-gray-300"
                                  }`}
                                />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Your Review
                        </label>
                        <Textarea
                          value={reviewText}
                          onChange={(e) => setReviewText(e.target.value)}
                          className="mt-1"
                          placeholder="Share your thoughts about this product"
                          rows={4}
                        />
                      </div>

                      {/* Image Upload Section */}
                      <ReviewImageUpload
                        images={reviewImages}
                        existingImages={existingReviewImages}
                        onImagesChange={setReviewImages}
                        onExistingImagesChange={setExistingReviewImages}
                        onImageMarkedForDeletion={(imageUrl) => {
                          setImagesToDelete((prev) => [...prev, imageUrl]);
                        }}
                        maxImages={5}
                        disabled={isSubmitting}
                      />
                      <Button
                        onClick={handleSubmitReview}
                        className="bg-jamaica-green text-white hover:bg-jamaica-green/90"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <LoadingSpinner size="sm" />
                            <span className="ml-2">
                              {editingReviewId !== null
                                ? "Updating..."
                                : "Submitting..."}
                            </span>
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            {editingReviewId !== null
                              ? "Update Review"
                              : "Submit Review"}
                          </>
                        )}
                      </Button>
                      {editingReviewId !== null && (
                        <Button
                          variant="outline"
                          className="ml-2"
                          onClick={() => {
                            setEditingReviewId(null);
                            setReviewText("");
                            setReviewRating(5);
                            setReviewImages([]);
                            setExistingReviewImages([]);
                            setImagesToDelete([]);
                            setActiveTab("reviews");
                          }}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  )}
                </TabsContent>
              )}
            </Tabs>
          </div>

          {/* You May Also Like Section */}
          <div className="mt-24">
            <p className="text-2xl font-bold text-gray-900 mb-6">
              You May Also Like
            </p>
            <Carousel
              opts={{
                align: "start",
              }}
              className="w-full"
            >
              <CarouselContent>
                {relatedProducts.map((relatedProduct) => (
                  <CarouselItem
                    key={relatedProduct.id}
                    className="basis-1/2 md:basis-1/2 lg:basis-1/4 py-2"
                  >
                    <ProductCard 
                      product={relatedProduct}
                      className="h-full group"
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="left-1" />
              <CarouselNext className="right-1" />
            </Carousel>
          </div>
        </div>
      </main>

      {/* Image Preview Modal */}
      <ImagePreviewModal
        images={previewImages}
        initialIndex={previewImageIndex}
        isOpen={isPreviewModalOpen}
        onOpenChange={setIsPreviewModalOpen}
        title="Review Images"
        description="Customer review photos"
      />

      {/* Delete Review Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Review
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600">
              Are you sure you want to delete your review? This action cannot be
              undone and your review will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={cancelDeleteReview}
              disabled={isSubmitting}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteReview}
              disabled={isSubmitting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isSubmitting ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">Deleting...</span>
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Review
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Sticky Mobile Add to Cart - Only visible on mobile */}
      {isMobile && (
        <div className="fixed bottom-[4.5rem] left-0 right-0 bg-white border-t border-gray-200 z-30">
          <div className="flex items-center gap-3 p-4">
            {/* Mobile Quantity Selector */}
            <div className="flex items-center">
              <button
                onClick={() => handleQuantityChange(-1)}
                className="rounded-l-md bg-gray-100 px-2 py-2 text-gray-600 hover:bg-gray-200 transition-colors"
              >
                <Minus className="h-4 w-4" />
              </button>
              {editingQuantity ? (
                <input
                  type="text"
                  value={tempQuantity}
                  onChange={(e) => handleQuantityInputChange(e.target.value)}
                  onBlur={handleQuantitySubmit}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleQuantitySubmit();
                    } else if (e.key === "Escape") {
                      handleQuantityCancel();
                    }
                  }}
                  className="border-y border-gray-200 px-3 py-2 bg-white w-[3rem] text-center text-sm font-medium outline-none focus:ring-0"
                  autoFocus
                />
              ) : (
                <div
                  className="border-y border-gray-200 px-3 py-2 bg-white min-w-[3rem] text-center text-sm font-medium cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={handleQuantityClick}
                >
                  {quantity}
                </div>
              )}
              <button
                onClick={() => handleQuantityChange(1)}
                className="rounded-r-md bg-gray-100 px-2 py-2 text-gray-600 hover:bg-gray-200 transition-colors"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>

            {/* Mobile Add to Cart Button */}
            {(() => {
              const stockStatus = getStockStatus(product);
              return (
                <Button
                  onClick={handleAddToCart}
                  disabled={stockStatus.isOutOfStock}
                  className={`flex-1 py-3 font-semibold ${
                    stockStatus.isOutOfStock
                      ? "bg-gray-400 text-gray-600 cursor-not-allowed hover:bg-gray-400"
                      : "bg-jamaica-green text-white hover:bg-jamaica-green/90"
                  }`}
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  {stockStatus.isOutOfStock ? "Out of Stock" : "Add to Cart"}
                </Button>
              );
            })()}
          </div>
        </div>
      )}

      {/* Scroll Down FAB - Only visible when at top of page */}
      <ScrollDownFAB scrollTarget="#image-gallery" />
    </div>
  );
};

export default ProductDetail;
